import { CardFinish, FINISH_LABELS, FINISH_ORDER } from './finishes';

/**
 * The Finish filter (offer builder focus mode, Asana 1219788320590626 Decision 7).
 *
 * Filtering itself runs server-side, before pagination; these helpers only
 * decide what reaches the wire and what the token chips say. None checked means
 * every finish, so nothing is sent; more than one checked is an OR, sent as one
 * comma list in canonical order so equal selections share one RTK cache key.
 */

/** Per-finish facet counts the server returns beside a page: the count you get by ticking that box. */
export type FinishCounts = Record<CardFinish, number>;

/** Canonical-order copy with duplicates dropped. */
export const normalizeFinishes = (finishes: readonly CardFinish[]): CardFinish[] => {
  const present = new Set(finishes);
  return FINISH_ORDER.filter((f) => present.has(f));
};

/** Wire value for `finish`: undefined when nothing is checked (all finishes), else `nonfoil,foil`. */
export const toFinishParam = (finishes: readonly CardFinish[]): string | undefined => {
  const canonical = normalizeFinishes(finishes);
  return canonical.length > 0 ? canonical.join(',') : undefined;
};

export const toggleFinish = (finishes: readonly CardFinish[], finish: CardFinish): CardFinish[] =>
  finishes.includes(finish)
    ? finishes.filter((f) => f !== finish)
    : normalizeFinishes([...finishes, finish]);

/** Pure removal of one finish; the others stay checked. */
export const removeFinish = (finishes: readonly CardFinish[], finish: CardFinish): CardFinish[] =>
  finishes.filter((f) => f !== finish);

/** Token id for one checked finish; the token row keys its mark icon off this prefix. */
export const FINISH_TOKEN_PREFIX = 'finish-';
export const finishTokenId = (finish: CardFinish): string => `${FINISH_TOKEN_PREFIX}${finish}`;
export const finishFromTokenId = (id: string): CardFinish | null => {
  if (!id.startsWith(FINISH_TOKEN_PREFIX)) return null;
  const finish = id.slice(FINISH_TOKEN_PREFIX.length);
  return (FINISH_ORDER as readonly string[]).includes(finish) ? (finish as CardFinish) : null;
};

export const finishTokenLabel = (finish: CardFinish): string => FINISH_LABELS[finish];
